
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

// =====================================================================================
// config
// =====================================================================================
const SHOP = window.GLASS_SHOP || null;
const DEV = !SHOP;
const params = new URLSearchParams(location.search);

// per-glass constants (mm), Blair's calipers; lathe profile as on the live upload page
const GLASSES = {
  maitai: { label: 'Mai Tai glass', short: 'Mai Tai', botR: 34.99, topR: 45.65, H: 114.8,
            print: { H: 90, W: 284, yBot: 19, yTop: 109 }, sku: 'GL-MAITAI-CUSTOM' },
  zombie: { label: 'Zombie glass', short: 'Zombie', botR: 28.85, topR: 31.78, H: 181.3,
            print: { H: 151, W: 196, yBot: 15, yTop: 166 }, sku: 'GL-ZOMBIE-CUSTOM' }
};
const SAFE = 3;                      // mm kept clear at the top and bottom of the band
const PRINT_DPI = [600, 450, 300];   // first that fits the request budget wins; never below 300
const BUDGET = 12 * 1024 * 1024;     // whole add-to-cart request (HOOK-CONTRACT.md)
const LIMITS = { elements: 30, texts: 3, lines: 3, chars: 30, uploads: 3 };
const DEV_PRICE = 19.99, DEV_BREAKS = [{ min: 4, price: 17.99 }, { min: 8, price: 16.99 }, { min: 12, price: 15.99 }];

// fixed brand palette for one-colour pieces and text; designs store the id
const PALETTE = [
  { id: 'ink', hex: '#2b1d12', name: 'Ink' },
  { id: 'tapa', hex: '#4f2c1d', name: 'Tapa brown' },
  { id: 'lava', hex: '#c8452a', name: 'Lava' },
  { id: 'gold', hex: '#c9a227', name: 'Gold' },
  { id: 'palm', hex: '#3e6b4f', name: 'Palm' },
  { id: 'lagoon', hex: '#0e3f42', name: 'Lagoon' },
  { id: 'paper', hex: '#f1e3bf', name: 'Paper' },
  { id: 'white', hex: '#ffffff', name: 'White' }
];
// a colour is a palette id, or (text and borders only) any '#rrggbb' the customer picks
const HEX_RE = /^#[0-9a-f]{6}$/i;
const hexOf = c => HEX_RE.test(c || '') ? c.toLowerCase() : (PALETTE.find(p => p.id === c) || PALETTE[0]).hex;

const FONTS = [
  { id: 'chicle', family: 'Chicle', name: 'Chicle' },
  { id: 'hanalei', family: 'Hanalei Fill', name: 'Hanalei' },
  { id: 'spicy', family: 'Spicy Rice', name: 'Spicy Rice' },
  { id: 'alfa', family: 'Alfa Slab One', name: 'Alfa Slab' },
  { id: 'righteous', family: 'Righteous', name: 'Righteous' },
  { id: 'bungee', family: 'Bungee', name: 'Bungee' },
  { id: 'oswald', family: 'Oswald', weight: 600, name: 'Oswald' },
  { id: 'spectral', family: 'Spectral', weight: 600, style: 'italic', name: 'Spectral' }
];
const fontOf = id => FONTS.find(f => f.id === id) || FONTS[0];
const fontCSS = (f, px) => `${f.style || 'normal'} ${f.weight || 400} ${px}px "${f.family}"`;
const LINE_H = 1.12;

// procedural borders: one period is `pf` × the border's height; drawn as vector paths so they
// stay sharp at any resolution, and the period always divides the wrap so the seam is invisible
const BORDERS = [
  { id: 'rule', name: 'Rule', pf: 4, h: 1.5 },
  { id: 'double', name: 'Double rule', pf: 4, h: 4 },
  { id: 'teeth', name: 'Shark teeth', pf: 1, h: 6 },
  { id: 'zigzag', name: 'Zigzag', pf: 1.3, h: 5 },
  { id: 'wave', name: 'Wave', pf: 2.6, h: 5 },
  { id: 'scallop', name: 'Scallop', pf: 2, h: 4 },
  { id: 'dots', name: 'Dots', pf: 1.8, h: 3 },
  { id: 'diamonds', name: 'Diamonds', pf: 1.4, h: 6 },
  { id: 'tapa', name: 'Tapa band', pf: 0.9, h: 8 }
];
const borderOf = id => BORDERS.find(b => b.id === id) || BORDERS[0];

// =====================================================================================
// state
// =====================================================================================
let glassKey = GLASSES[params.get('glass')] ? params.get('glass') : 'maitai';
let G = GLASSES[glassKey];
let W = G.print.W, H = G.print.H;
let elements = [];                  // the design, bottom layer first
let selected = null;                // uid of the selected element
let uidSeq = 1;
const uploads = new Map();          // upload key → { file, name, img, preview, w, h }
let uploadSeq = 1;
const lib = { items: [], byId: new Map(), base: null, ok: false };
const previews = new Map();         // url → loaded preview image (editor + 3D)

const $ = s => document.querySelector(s);
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const mod = (a, n) => ((a % n) + n) % n;
const r1 = v => Math.round(v * 10) / 10;
const deg = r => r * 180 / Math.PI, rad = d => d * Math.PI / 180;

// =====================================================================================
// library
// =====================================================================================
const manifestUrl = new URL((SHOP && SHOP.manifestUrl) || 'library/manifest.json', location.href).href;
const libUrl = p => new URL(p, manifestUrl).href;

function loadImage(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    if (new URL(url, location.href).origin !== location.origin) img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('image failed: ' + url));
    img.src = url;
  });
}
async function previewOf(item) {
  const url = libUrl(item.preview);
  if (!previews.has(url)) previews.set(url, loadImage(url));
  return previews.get(url);
}
async function loadLibrary() {
  try {
    const r = await fetch(manifestUrl, { cache: 'no-cache' });
    if (!r.ok) throw new Error('manifest ' + r.status);
    const m = await r.json();
    lib.items = (m.items || []).filter(i => !i.glasses || i.glasses.includes('maitai') || i.glasses.includes('zombie'));
    lib.items.forEach(i => lib.byId.set(i.id, i));
    lib.ok = true;
  } catch (e) {
    console.warn('library unavailable', e);
    lib.ok = false;
  }
}
// width / height of a library piece
function aspectOf(item) {
  if (item.px) return item.px[0] / item.px[1];
  const img = item._img;
  return img && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
}

// editor-resolution source for an element (sync; must be preloaded). Tinted copies cached.
const tintCache = new Map();
function tinted(src, hex, w, h) {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h));
  const x = c.getContext('2d');
  x.imageSmoothingQuality = 'high';
  x.drawImage(src, 0, 0, c.width, c.height);
  x.globalCompositeOperation = 'source-in';
  x.fillStyle = hex;
  x.fillRect(0, 0, c.width, c.height);
  return c;
}
// the colour a one-colour piece is drawn in (average of its solid pixels), for the "Original" swatch
function originalHex(item) {
  if (item._origHex !== undefined) return item._origHex;
  if (!item._img) return null;
  const c = document.createElement('canvas'); c.width = c.height = 48;
  const x = c.getContext('2d', { willReadFrequently: true });
  x.drawImage(item._img, 0, 0, 48, 48);
  const d = x.getImageData(0, 0, 48, 48).data;
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < d.length; i += 4) if (d[i + 3] > 200) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
  const h = v => Math.round(v / n).toString(16).padStart(2, '0');
  item._origHex = n ? '#' + h(r) + h(g) + h(b) : '#000000';
  return item._origHex;
}
function previewSource(el) {
  if (el.kind === 'upload') { const u = uploads.get(el.upload); return u ? u.preview : null; }
  const item = lib.byId.get(el.id);
  if (!item || !item._img) return null;
  if (!el.color || !item.recolor) return item._img;
  const key = item.id + '|' + el.color;
  if (!tintCache.has(key)) tintCache.set(key, tinted(item._img, hexOf(el.color), item._img.naturalWidth, item._img.naturalHeight));
  return tintCache.get(key);
}

// =====================================================================================
// the scene renderer: used by the editor, the 3D texture and the print file
//   ctx is set up so that 1 unit = 1 mm (plus any offset); o = { W, x0, x1, src, skip, alphaOne }
// =====================================================================================
function halfX(el) { const a = rad(el.rot || 0); return (Math.abs(el.w * Math.cos(a)) + Math.abs(el.h * Math.sin(a))) / 2; }
function halfY(el) { const a = rad(el.rot || 0); return (Math.abs(el.w * Math.sin(a)) + Math.abs(el.h * Math.cos(a))) / 2; }

function drawScene(ctx, o) {
  for (const el of o.list || elements) {
    if (o.skip && o.skip(el)) continue;
    const alpha = o.alphaOne ? 1 : (el.opacity ?? 1);
    if (el.kind === 'pattern') drawPattern(ctx, el, o, alpha);
    else if (el.kind === 'border') drawBorder(ctx, el, o, alpha);
    else drawPlaced(ctx, el, o, alpha);
  }
}

function drawPlaced(ctx, el, o, alpha) {
  const hw = halfX(el);
  for (const k of [-1, 0, 1]) {               // the copies either side of the seam
    const cx = el.x + k * o.W;
    if (cx + hw < o.x0 || cx - hw > o.x1) continue;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(cx, el.y);
    ctx.rotate(rad(el.rot || 0));
    if (el.flipX) ctx.scale(-1, 1);
    if (el.kind === 'text') drawText(ctx, el);
    else {
      const s = o.src(el);
      if (s) ctx.drawImage(s, -el.w / 2, -el.h / 2, el.w, el.h);
    }
    ctx.restore();
  }
}

function drawText(ctx, el) {
  const f = fontOf(el.font), lines = el.text.split('\n'), lh = el.size * LINE_H;
  ctx.fillStyle = hexOf(el.color || 'ink');
  ctx.font = fontCSS(f, el.size);
  ctx.textBaseline = 'middle';
  ctx.textAlign = el.align || 'center';
  const ax = el.align === 'left' ? -el.w / 2 : el.align === 'right' ? el.w / 2 : 0;
  lines.forEach((ln, i) => ctx.fillText(ln, ax, (i - (lines.length - 1) / 2) * lh));
}
const measureCtx = document.createElement('canvas').getContext('2d');
function measureText(el) {
  const f = fontOf(el.font), lines = el.text.split('\n');
  measureCtx.font = fontCSS(f, 100);
  const wMax = Math.max(1, ...lines.map(l => measureCtx.measureText(l).width));
  el.w = wMax / 100 * el.size * 1.04;
  el.h = lines.length * el.size * LINE_H;
}

// tiles at whole-pixel edges so there are no hairlines; the tile count divides the wrap
function patternGeom(el, Wm) {
  const item = lib.byId.get(el.id);
  const a = item ? aspectOf(item) : 1;
  const n = Math.max(1, Math.round(Wm / (el.tile * a)));
  return { n, tw: Wm / n, th: el.tile, h: el.tile * (el.rows || 1) };
}
function drawPattern(ctx, el, o, alpha) {
  const src = o.src(el);
  if (!src) return;
  const { tw, th } = patternGeom(el, o.W), rows = el.rows || 1;
  const m = ctx.getTransform();
  const X = v => m.a * v + m.e, Y = v => m.d * v + m.f;
  const top = el.y - (th * rows) / 2, off = mod(el.dx || 0, tw);
  const i0 = Math.floor((o.x0 - off) / tw) - 1, i1 = Math.ceil((o.x1 - off) / tw);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = alpha;
  for (let r = 0; r < rows; r++) {
    const ya = Math.round(Y(top + r * th)), yb = Math.round(Y(top + (r + 1) * th));
    for (let i = i0; i <= i1; i++) {
      const xa = Math.round(X(off + i * tw)), xb = Math.round(X(off + (i + 1) * tw));
      if (xb > xa && yb > ya) ctx.drawImage(src, xa, ya, xb - xa, yb - ya);
    }
  }
  ctx.restore();
}

function drawBorder(ctx, el, o, alpha) {
  const b = borderOf(el.id), h = el.h, top = el.y - h / 2, bot = el.y + h / 2;
  const n = Math.max(1, Math.round(o.W / (h * b.pf))), p = o.W / n;
  const k0 = Math.floor(o.x0 / p) - 1, k1 = Math.ceil(o.x1 / p) + 1;
  const xa = k0 * p, xb = (k1 + 1) * p;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = ctx.strokeStyle = hexOf(el.color || 'ink');
  ctx.beginPath();
  if (b.id === 'rule') ctx.rect(xa, top, xb - xa, h);
  else if (b.id === 'double') { const t = h * 0.3; ctx.rect(xa, top, xb - xa, t); ctx.rect(xa, bot - t, xb - xa, t); }
  else if (b.id === 'teeth') for (let k = k0; k <= k1; k++) { const x = k * p; ctx.moveTo(x, bot); ctx.lineTo(x + p / 2, top); ctx.lineTo(x + p, bot); ctx.closePath(); }
  else if (b.id === 'zigzag' || b.id === 'wave') {
    const lw = h * 0.24, amp = (h - lw) / 2;
    ctx.lineWidth = lw; ctx.lineJoin = 'miter'; ctx.lineCap = 'butt';
    if (b.id === 'zigzag') { const lwz = lw * 1.25; ctx.lineWidth = lwz; const az = (h - lwz) / 2;
      for (let k = k0; k <= k1; k++) { const x = k * p; if (k === k0) ctx.moveTo(x, el.y + az); ctx.lineTo(x + p / 2, el.y - az); ctx.lineTo(x + p, el.y + az); } }
    else { const steps = 24;
      for (let k = k0; k <= k1; k++) for (let s = 0; s <= steps; s++) { const x = k * p + p * s / steps, y = el.y - amp * Math.sin(2 * Math.PI * s / steps);
        if (k === k0 && s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); } }
    ctx.stroke();
  }
  else if (b.id === 'scallop') for (let k = k0; k <= k1; k++) { const cx = k * p + p / 2; ctx.moveTo(cx + p / 2, bot); ctx.arc(cx, bot, p / 2, 0, Math.PI, true); ctx.closePath(); }
  else if (b.id === 'dots') for (let k = k0; k <= k1; k++) { const cx = k * p + p / 2; ctx.moveTo(cx + h / 2, el.y); ctx.arc(cx, el.y, h / 2, 0, 2 * Math.PI); }
  else if (b.id === 'diamonds') for (let k = k0; k <= k1; k++) { const cx = k * p + p / 2, hw = p * 0.4; ctx.moveTo(cx - hw, el.y); ctx.lineTo(cx, top); ctx.lineTo(cx + hw, el.y); ctx.lineTo(cx, bot); ctx.closePath(); }
  else if (b.id === 'tapa') {
    const t = h * 0.13, it = top + t * 1.6, ib = bot - t * 1.6;
    ctx.rect(xa, top, xb - xa, t); ctx.rect(xa, bot - t, xb - xa, t);
    for (let k = k0; k <= k1; k++) { const x = k * p;
      if (k % 2 === 0) { ctx.moveTo(x + p * 0.06, ib); ctx.lineTo(x + p / 2, it); ctx.lineTo(x + p * 0.94, ib); }
      else { ctx.moveTo(x + p * 0.06, it); ctx.lineTo(x + p / 2, ib); ctx.lineTo(x + p * 0.94, it); }
      ctx.closePath(); }
  }
  if (b.id !== 'zigzag' && b.id !== 'wave') ctx.fill();
  ctx.restore();
}

// =====================================================================================
// elements
// =====================================================================================
function elName(el) {
  if (el.kind === 'text') return 'Text: ' + (el.text.split('\n')[0] || '…');
  if (el.kind === 'border') return borderOf(el.id).name + ' border';
  if (el.kind === 'upload') return 'Your image';
  const it = lib.byId.get(el.id);
  return it ? it.name : 'Art';
}
function safeH() { return H - 2 * SAFE; }

// keep every piece inside the safe zone vertically; x wraps
function fit(el) {
  if (el.kind === 'pattern') {
    el.tile = clamp(el.tile, 4, safeH());
    el.rows = clamp(Math.round(el.rows || 1), 1, Math.max(1, Math.floor(safeH() / el.tile)));
    const h = el.tile * el.rows;
    el.y = clamp(el.y, SAFE + h / 2, H - SAFE - h / 2);
    el.dx = r1(mod(el.dx || 0, W));
    return el;
  }
  if (el.kind === 'border') {
    el.h = clamp(el.h, 1, Math.min(30, safeH()));
    el.y = clamp(el.y, SAFE + el.h / 2, H - SAFE - el.h / 2);
    return el;
  }
  if (el.kind === 'text') measureText(el);
  let hy = halfY(el);
  if (hy * 2 > safeH()) {                     // too tall for the band: shrink to fit
    const s = safeH() / (hy * 2);
    if (el.kind === 'text') { el.size *= s; measureText(el); } else { el.w *= s; el.h *= s; }
    hy = halfY(el);
  }
  const hx = halfX(el);
  if (hx * 2 > W) { const s = W / (hx * 2); if (el.kind === 'text') { el.size *= s; measureText(el); } else { el.w *= s; el.h *= s; } }
  el.y = clamp(el.y, SAFE + hy, H - SAFE - hy);
  el.x = mod(el.x, W);
  return el;
}

function visibleCenterX() {
  const sc = $('#bandScroll');
  if (!view.ppm || !sc.clientWidth || sc.scrollWidth <= sc.clientWidth + 1) return W / 2;
  return mod((sc.scrollLeft + sc.clientWidth / 2) / view.ppm - view.pad, W);
}

function canAdd(kind) {
  if (elements.length >= LIMITS.elements) return say('That’s as many pieces as one glass can hold. Remove one to add another.');
  if (kind === 'text' && elements.filter(e => e.kind === 'text').length >= LIMITS.texts) return say('Three lines of text per glass is the limit. Edit one of them instead.');
  if (kind === 'upload' && uploads.size >= LIMITS.uploads) return say('Three of your own images per glass is the limit.');
  return true;
}

async function addLibrary(item) {
  if (!canAdd(item.kind)) return;
  try { item._img = await previewOf(item); } catch (e) { return say('That piece didn’t load. Try again in a moment.'); }
  let el;
  if (item.kind === 'pattern') {
    const tile = item.id.startsWith('tapa') ? 10 : 30;
    el = { kind: 'pattern', id: item.id, y: H / 2, tile, rows: 1, dx: 0 };
  } else {
    const a = aspectOf(item);
    let h = Math.min(safeH() * 0.8, 70), w = h * a;
    if (w > W * 0.6) { w = W * 0.6; h = w / a; }
    el = { kind: 'art', id: item.id, x: visibleCenterX(), y: H / 2, w, h, rot: 0 };
  }
  insert(el);
}
function addBorder(b) {
  if (!canAdd('border')) return;
  const tops = elements.filter(e => e.kind === 'border').length;
  const y = tops % 2 === 0 ? SAFE + b.h / 2 + 1 : H - SAFE - b.h / 2 - 1;
  insert({ kind: 'border', id: b.id, y, h: b.h, color: 'ink' });
}
async function addText() {
  if (!canAdd('text')) return;
  const el = { kind: 'text', text: 'Aloha', font: 'chicle', size: Math.min(18, safeH() * 0.3), align: 'center',
               color: 'ink', x: visibleCenterX(), y: H / 2, rot: 0 };
  await ensureFont(el.font, el.text);
  insert(el);
  setTimeout(() => { const t = $('#insp textarea'); if (t) { t.focus(); t.select(); } }, 30);
}
function insert(el) {
  el.uid = uidSeq++;
  if (el.opacity === undefined) el.opacity = 1;
  fit(el);
  // patterns and borders go under the art, art on top
  if (el.kind === 'pattern' || el.kind === 'border') {
    const firstPlaced = elements.findIndex(e => e.kind !== 'pattern' && e.kind !== 'border');
    elements.splice(firstPlaced < 0 ? elements.length : firstPlaced, 0, el);
  } else elements.push(el);
  select(el.uid);
  commit();
  say(elName(el) + ' added.');
}
const byUid = uid => elements.find(e => e.uid === uid);

async function ensureFont(id, sample) {
  const f = fontOf(id);
  try { await document.fonts.load(fontCSS(f, 40), sample || 'Aa'); } catch (e) {}
}

// =====================================================================================
// uploads
// =====================================================================================
async function addUpload(file) {
  if (!file || !canAdd('upload')) return;
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) return say('Please choose a PNG, JPG or WebP image.');
  let img;
  try { img = await loadImage(URL.createObjectURL(file)); } catch (e) { return say('That image didn’t open. Try a PNG or JPG.'); }
  const max = 1600, s = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const pv = document.createElement('canvas');
  pv.width = Math.round(img.naturalWidth * s); pv.height = Math.round(img.naturalHeight * s);
  pv.getContext('2d').drawImage(img, 0, 0, pv.width, pv.height);
  const key = 'u' + uploadSeq++;
  uploads.set(key, { file, name: file.name, img, preview: pv, w: img.naturalWidth, h: img.naturalHeight });
  const a = img.naturalWidth / img.naturalHeight;
  let h = Math.min(safeH() * 0.8, 70), w = h * a;
  if (w > W * 0.7) { w = W * 0.7; h = w / a; }
  insert({ kind: 'upload', upload: key, x: visibleCenterX(), y: H / 2, w, h, rot: 0 });
}
// effective print DPI of a raster piece at its placed size
function dpiOf(el) {
  if (el.kind === 'upload') { const u = uploads.get(el.upload); return u ? u.w / (el.w / 25.4) : 0; }
  const it = lib.byId.get(el.id);
  if (el.kind === 'art' && it && it.px) return it.px[0] / (el.w / 25.4);
  if (el.kind === 'pattern' && it && it.px) return it.px[1] / (el.tile / 25.4);
  return Infinity;
}

// =====================================================================================
// history (undo / redo) and autosave
// =====================================================================================
const hist = { stack: [], i: -1 };
const snap = () => JSON.stringify(elements);
function commit() {
  const s = snap();
  if (hist.stack[hist.i] === s) return;
  hist.stack = hist.stack.slice(0, hist.i + 1);
  hist.stack.push(s);
  if (hist.stack.length > 80) hist.stack.shift();
  hist.i = hist.stack.length - 1;
  changed();
}
function restore(s) {
  elements = JSON.parse(s);
  if (!byUid(selected)) selected = null;
  changed(true);
}
function undo() { if (hist.i > 0) { hist.i--; restore(hist.stack[hist.i]); say('Undone.'); } }
function redo() { if (hist.i < hist.stack.length - 1) { hist.i++; restore(hist.stack[hist.i]); say('Redone.'); } }

const SAVE_KEY = 'bgr-glass-art-v1';
function autosave() {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({ glass: glassKey, elements: elements.filter(e => e.kind !== 'upload') }));
  } catch (e) {}
}
function loadSaved() {
  try { return JSON.parse(localStorage.getItem(SAVE_KEY) || 'null'); } catch (e) { return null; }
}

// anything that changes the design: redraw, re-render panels, and void the "final" tick
let designVersion = 0;
function changed(fromHistory) {
  designVersion++;
  $('#undoBtn').disabled = hist.i <= 0;
  $('#redoBtn').disabled = hist.i >= hist.stack.length - 1;
  if ($('#ack').checked) { $('#ack').checked = false; updateAtc(); }
  drawBand();
  requestTexture();
  renderInspector();
  autosave();
  if (DEV) $('#devJson').textContent = JSON.stringify(designJSON(600), null, 1);
}

// =====================================================================================
// band editor
// =====================================================================================
const band = $('#band');
const bctx = band.getContext('2d');
const view = { zoom: 1, ppm: 1, pad: 0, cssW: 0, cssH: 0, bg: 0 };
const BACKDROPS = [
  { name: 'Lagoon', top: '#174f53', bot: '#0b3134' },
  { name: 'Paper', top: '#f6ecd2', bot: '#e8d7ad' },
  { name: 'Amber', top: '#d9902f', bot: '#a8601b' }
];

function layoutBand() {
  const sc = $('#bandScroll');
  view.pad = W * 0.06;
  sc.style.overflowX = view.zoom > 1 ? 'auto' : 'hidden';
  view.cssW = Math.max(200, Math.floor(sc.clientWidth * view.zoom));
  view.ppm = view.cssW / (W + 2 * view.pad);
  view.cssH = Math.round(H * view.ppm);
  const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
  band.style.width = view.cssW + 'px';
  band.style.height = view.cssH + 'px';
  band.width = Math.round(view.cssW * dpr);
  band.height = Math.round(view.cssH * dpr);
  view.dpr = dpr;
  // phones: the stage stays pinned while the library scrolls, unless it would fill the screen
  const st = $('#stage');
  st.classList.toggle('no-stick', st.offsetHeight > window.innerHeight * 0.58);
  document.documentElement.style.setProperty('--stage-h', (getComputedStyle(st).position === 'sticky' ? st.offsetHeight : 0) + 'px');
  drawBand();
}

function drawBand() {
  if (!view.ppm) return;
  const ctx = bctx, dpr = view.dpr, s = view.ppm * dpr;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  const bd = BACKDROPS[view.bg];
  const g = ctx.createLinearGradient(0, 0, 0, band.height);
  g.addColorStop(0, bd.top); g.addColorStop(1, bd.bot);
  ctx.fillStyle = g; ctx.fillRect(0, 0, band.width, band.height);
  ctx.setTransform(s, 0, 0, s, view.pad * s, 0);
  drawScene(ctx, { W, x0: -view.pad, x1: W + view.pad, src: previewSource });

  // past the seam: dim the repeated strips either side
  ctx.fillStyle = view.bg === 0 ? 'rgba(9,43,46,.55)' : 'rgba(241,227,191,.6)';
  ctx.fillRect(-view.pad - 1, 0, view.pad + 1, H);
  ctx.fillRect(W, 0, view.pad + 1, H);
  const lw = 1 / view.ppm;
  const guide = view.bg === 0 ? 'rgba(241,227,191,.75)' : 'rgba(43,29,18,.7)';
  // safe margin guides
  ctx.save();
  ctx.strokeStyle = guide; ctx.lineWidth = lw; ctx.setLineDash([4 * lw, 4 * lw]);
  ctx.beginPath(); ctx.moveTo(0, SAFE); ctx.lineTo(W, SAFE); ctx.moveTo(0, H - SAFE); ctx.lineTo(W, H - SAFE); ctx.stroke();
  ctx.restore();
  // the seam, at both ends
  ctx.save();
  ctx.strokeStyle = '#c8452a'; ctx.lineWidth = 2 * lw; ctx.setLineDash([6 * lw, 4 * lw]);
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, H); ctx.moveTo(W, 0); ctx.lineTo(W, H); ctx.stroke();
  ctx.setLineDash([]);
  ctx.font = `600 ${10 * lw}px Oswald, sans-serif`;
  ctx.textBaseline = 'top';
  for (const x of [0, W]) {
    const t = 'SEAM', tw = ctx.measureText(t).width + 8 * lw;
    ctx.fillStyle = '#c8452a'; ctx.fillRect(x - tw / 2, 2 * lw, tw, 14 * lw);
    ctx.fillStyle = '#f1e3bf'; ctx.textAlign = 'center'; ctx.fillText(t, x, 4 * lw);
  }
  ctx.restore();

  // selection
  const el = byUid(selected);
  if (el) drawSelection(ctx, el, lw);
}

function instancesInView(el) {
  if (el.kind === 'pattern' || el.kind === 'border') return [0];
  const hw = halfX(el);
  return [-1, 0, 1].filter(k => el.x + k * W + hw >= -view.pad && el.x + k * W - hw <= W + view.pad);
}
function handlePoints(el, k) {
  const a = rad(el.rot || 0), c = Math.cos(a), s = Math.sin(a), cx = el.x + k * W;
  const P = (lx, ly) => ({ x: cx + lx * c - ly * s, y: el.y + lx * s + ly * c });
  const ext = 18 / view.ppm;
  return { corners: [P(-el.w / 2, -el.h / 2), P(el.w / 2, -el.h / 2), P(el.w / 2, el.h / 2), P(-el.w / 2, el.h / 2)],
           scale: P(el.w / 2, el.h / 2), rotate: P(0, -el.h / 2 - ext), top: P(0, -el.h / 2) };
}
function drawSelection(ctx, el, lw) {
  ctx.save();
  ctx.strokeStyle = '#c9a227'; ctx.lineWidth = 2 * lw;
  if (el.kind === 'pattern' || el.kind === 'border') {
    const h = el.kind === 'pattern' ? patternGeom(el, W).h : el.h;
    ctx.setLineDash([5 * lw, 3 * lw]);
    ctx.strokeRect(-view.pad, el.y - h / 2, W + 2 * view.pad, h);
    ctx.restore();
    return;
  }
  for (const k of instancesInView(el)) {
    const hp = handlePoints(el, k);
    ctx.setLineDash(k === 0 ? [] : [5 * lw, 3 * lw]);
    ctx.beginPath(); hp.corners.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(hp.top.x, hp.top.y); ctx.lineTo(hp.rotate.x, hp.rotate.y); ctx.stroke();
    const r = 7 * lw;
    for (const [p, round] of [[hp.scale, false], [hp.rotate, true]]) {
      ctx.fillStyle = '#f1e3bf'; ctx.beginPath();
      if (round) ctx.arc(p.x, p.y, r, 0, 2 * Math.PI); else ctx.rect(p.x - r, p.y - r, 2 * r, 2 * r);
      ctx.fill(); ctx.stroke();
    }
  }
  ctx.restore();
}

// pointer → mm
function toMM(e) {
  const b = band.getBoundingClientRect();
  return { x: (e.clientX - b.left) / view.ppm - view.pad, y: (e.clientY - b.top) / view.ppm };
}
function hitElement(p) {
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i];
    if (el.kind === 'pattern' || el.kind === 'border') {
      const h = el.kind === 'pattern' ? patternGeom(el, W).h : Math.max(el.h, 4);
      if (Math.abs(p.y - el.y) <= h / 2) return { el, k: 0 };
      continue;
    }
    const a = rad(el.rot || 0), c = Math.cos(a), s = Math.sin(a), tol = 4 / view.ppm;
    for (const k of [0, -1, 1]) {
      const dx = p.x - (el.x + k * W), dy = p.y - el.y;
      const lx = dx * c + dy * s, ly = -dx * s + dy * c;
      if (Math.abs(lx) <= el.w / 2 + tol && Math.abs(ly) <= el.h / 2 + tol) return { el, k };
    }
  }
  return null;
}
function hitHandle(p) {
  const el = byUid(selected);
  if (!el || el.kind === 'pattern' || el.kind === 'border') return null;
  const r = 16 / view.ppm;
  for (const k of instancesInView(el)) {
    const hp = handlePoints(el, k);
    if (Math.hypot(p.x - hp.scale.x, p.y - hp.scale.y) <= r) return { mode: 'scale', el, k };
    if (Math.hypot(p.x - hp.rotate.x, p.y - hp.rotate.y) <= r) return { mode: 'rotate', el, k };
  }
  return null;
}

let drag = null, pointerIsDown = false;
band.addEventListener('pointerdown', e => {
  band.focus({ preventScroll: true });
  pointerIsDown = true;
  const p = toMM(e);
  let h = hitHandle(p);
  if (!h) {
    const hit = hitElement(p);
    if (!hit) { select(null); return; }
    select(hit.el.uid);
    h = { mode: hit.el.kind === 'pattern' || hit.el.kind === 'border' ? 'strip' : 'move', el: hit.el, k: hit.k };
  }
  const el = h.el, cx = el.x + h.k * W;
  drag = { ...h, start: p, orig: { ...el }, cx,
           d0: Math.hypot(p.x - cx, p.y - el.y), a0: Math.atan2(p.y - el.y, p.x - cx) };
  band.setPointerCapture(e.pointerId);
  e.preventDefault();
});
band.addEventListener('pointermove', e => {
  if (!drag) { band.style.cursor = hitHandle(toMM(e)) ? 'grab' : hitElement(toMM(e)) ? 'move' : 'default'; return; }
  const p = toMM(e), el = drag.el, o = drag.orig;
  if (drag.mode === 'move') { el.x = o.x + (p.x - drag.start.x); el.y = o.y + (p.y - drag.start.y); }
  else if (drag.mode === 'strip') {
    el.y = o.y + (p.y - drag.start.y);
    if (el.kind === 'pattern') el.dx = (o.dx || 0) + (p.x - drag.start.x);
  }
  else if (drag.mode === 'scale') {
    const f = Math.max(0.05, Math.hypot(p.x - drag.cx, p.y - el.y) / Math.max(1e-6, drag.d0));
    if (el.kind === 'text') el.size = clamp(o.size * f, 3, 120);
    else { el.w = Math.max(4, o.w * f); el.h = el.w * o.h / o.w; }
  }
  else if (drag.mode === 'rotate') {
    let a = deg(o.rot ? rad(o.rot) : 0) + deg(Math.atan2(p.y - el.y, p.x - drag.cx) - drag.a0);
    a = ((a + 540) % 360) - 180;
    for (const s of [-180, -90, 0, 90, 180]) if (Math.abs(a - s) < 4) a = s;
    el.rot = Math.round(a);
  }
  fit(el);
  drawBand();
  requestTexture();
  syncInspector();
});
function endDrag() {
  pointerIsDown = false;
  if (!drag) return;
  if (drag.el.x !== undefined) faceX(drag.el.x);
  drag = null;
  commit();
  if (revealPending) revealInspector();
}
band.addEventListener('pointerup', endDrag);
band.addEventListener('pointercancel', endDrag);
band.addEventListener('keydown', e => {
  const el = byUid(selected);
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); return; }
  if (e.key === 'Tab' && elements.length) {
    const i = elements.findIndex(x => x.uid === selected);
    const n = e.shiftKey ? i - 1 : i + 1;
    if (n >= 0 && n < elements.length) { e.preventDefault(); select(elements[n].uid); say(elName(elements[n]) + ' selected.'); }
    return;
  }
  if (!el) return;
  const step = e.shiftKey ? 5 : 1;
  const mv = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
  if (mv) {
    e.preventDefault();
    if (el.kind === 'pattern') { el.dx = (el.dx || 0) + mv[0]; el.y += mv[1]; }
    else if (el.kind === 'border') el.y += mv[1];
    else { el.x += mv[0]; el.y += mv[1]; }
    fit(el); commit();
  } else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeEl(el); }
  else if (e.key === 'Escape') select(null);
});

function select(uid) {
  if (selected === uid) return;
  selected = uid;
  const el = byUid(uid);
  if (el && el.x !== undefined) faceX(el.x);
  drawBand();
  renderInspector();
  if (el && !drag && !pointerIsDown) revealInspector(); else revealPending = !!el;
}
// bring the inspector into view (under the pinned stage on phones) if it's off screen
let revealPending = false;
function revealInspector() {
  revealPending = false;
  const r = insp.getBoundingClientRect(), top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--stage-h')) || 0;
  if (r.top < top || r.top > window.innerHeight - 120) insp.scrollIntoView({ block: 'start', behavior: REDUCED ? 'auto' : 'smooth' });
}
function removeEl(el) {
  elements = elements.filter(x => x !== el);
  if (el.kind === 'upload' && !elements.some(x => x.upload === el.upload)) { /* keep the source for undo */ }
  selected = null;
  commit();
  say(elName(el) + ' removed.');
}
function moveLayer(el, dir) {
  const i = elements.indexOf(el), j = clamp(i + dir, 0, elements.length - 1);
  if (i === j) return;
  elements.splice(i, 1); elements.splice(j, 0, el);
  commit();
}
function duplicate(el) {
  if (!canAdd(el.kind === 'upload' ? 'art' : el.kind)) return;
  const c = JSON.parse(JSON.stringify(el));
  delete c.uid;
  if (c.x !== undefined) c.x += 12; else c.y += 6;
  insert(c);
}

// =====================================================================================
// inspector
// =====================================================================================
const insp = $('#insp');
function slider(label, key, min, max, step, val, fmt, onInput) {
  return { label, key, min, max, step, val, fmt, onInput };
}
function renderInspector() {
  const el = byUid(selected);
  insp.hidden = !el;
  if (!el) { insp.innerHTML = ''; return; }
  const item = lib.byId.get(el.id);
  const recolor = el.kind === 'text' || el.kind === 'border' || !!(item && item.recolor);
  const anyColor = recolor;                 // every recolourable piece takes any colour (Blair, 2026-10-27)
  const sliders = [];
  if (el.kind === 'art' || el.kind === 'upload') {
    sliders.push(slider('Size', 'size', 8, el.kind === 'upload' ? W : Math.min(W, (item && item.mmW ? item.mmW * 2 : W)), 1, el.w, v => Math.round(v) + ' mm',
      (v) => { const a = el.h / el.w; el.w = v; el.h = v * a; }));
  }
  if (el.kind === 'text') sliders.push(slider('Size', 'tsize', 3, Math.min(80, safeH()), 0.5, el.size, v => Math.round(v) + ' mm', v => { el.size = v; }));
  if (el.kind === 'pattern') {
    sliders.push(slider('Size', 'tile', 4, Math.min(safeH(), 80), 0.5, el.tile, v => Math.round(v) + ' mm', v => { el.tile = v; }));
    sliders.push(slider('Rows', 'rows', 1, Math.max(1, Math.floor(safeH() / el.tile)), 1, el.rows || 1, v => String(v), v => { el.rows = v; }));
  }
  if (el.kind === 'border') sliders.push(slider('Thickness', 'bh', 1, Math.min(30, safeH()), 0.5, el.h, v => r1(v) + ' mm', v => { el.h = v; }));
  if (el.kind !== 'pattern' && el.kind !== 'border') sliders.push(slider('Turn', 'rot', -180, 180, 1, el.rot || 0, v => v + '°', v => { el.rot = v; }));
  if (el.kind !== 'border') sliders.push(slider('Across', 'x', 0, W, 1, el.kind === 'pattern' ? mod(el.dx || 0, W) : el.x, v => Math.round(v) + ' mm',
    v => { if (el.kind === 'pattern') el.dx = v; else el.x = v; }));
  sliders.push(slider('Height', 'y', 0, H, 0.5, el.y, v => Math.round(v) + ' mm', v => { el.y = v; }));
  sliders.push(slider('See-through', 'op', 30, 100, 5, Math.round((el.opacity ?? 1) * 100), v => (v >= 100 ? 'solid' : (100 - v) + '%'), v => { el.opacity = v / 100; }));

  let html = `<div class="insp__head"><p class="insp__title">${esc(elName(el))}</p>
    <button type="button" class="sbtn" id="ix-done">Done</button></div>`;
  if (el.kind === 'text') {
    html += `<label class="label" for="ix-text">Your words <span style="text-transform:none;letter-spacing:0">(up to ${LIMITS.lines} lines, ${LIMITS.chars} letters each)</span></label>
      <textarea class="txt" id="ix-text" rows="3" spellcheck="true">${esc(el.text)}</textarea>
      <span class="label">Lettering</span><div class="fonts" id="ix-fonts">${FONTS.map(f =>
        `<button type="button" class="fontbtn" data-font="${f.id}" aria-pressed="${f.id === el.font}" style="font-family:'${f.family}';font-weight:${f.weight || 400};font-style:${f.style || 'normal'}">${f.name}</button>`).join('')}</div>
      <div class="btnrow" role="group" aria-label="Line up">${['left', 'center', 'right'].map(a =>
        `<button type="button" class="sbtn ${el.align === a ? 'sbtn--fill' : ''}" data-align="${a}" aria-pressed="${el.align === a}">${a === 'center' ? 'Centre' : a[0].toUpperCase() + a.slice(1)}</button>`).join('')}</div>`;
  }
  if (recolor) {
    const orig = item ? originalHex(item) : null;     // one-colour art: its own colour, shown as a solid swatch
    html += `<span class="label">Colour</span><div class="swatches" id="ix-sw">` +
      (orig ? `<button type="button" class="sw sw--orig" data-color="" aria-pressed="${!el.color}" title="Original colour" aria-label="Original colour" style="background:${orig}"></button>` : '') +
      PALETTE.map(p => `<button type="button" class="sw" data-color="${p.id}" style="background:${p.hex}" aria-pressed="${el.color === p.id}" title="${p.name}" aria-label="${p.name}"></button>`).join('') +
      `</div>`;
    if (anyColor) {
      const custom = HEX_RE.test(el.color || ''), cur = el.color ? hexOf(el.color) : (orig || hexOf('ink'));
      html += `<div class="anycol">
        <label class="sw sw--any" aria-pressed="${custom}" title="Any colour"${custom ? ` style="background:${cur}"` : ''}>
          <input type="color" id="ix-any" value="${cur}" aria-label="Pick any colour"></label>
        <label class="sr" for="ix-hex">Colour code</label>
        <input type="text" class="hexin" id="ix-hex" value="${cur}" maxlength="7" spellcheck="false" autocomplete="off" inputmode="text">
        <span class="note" style="margin:0">Any colour you like</span></div>`;
    }
  }
  html += `<div style="margin-top:12px">` + sliders.map(s => `<div class="row"><label class="label" for="ix-${s.key}">${s.label}</label>
    <input type="range" id="ix-${s.key}" min="${s.min}" max="${s.max}" step="${s.step}" value="${s.val}">
    <span class="val" id="ix-${s.key}-v">${s.fmt(+s.val)}</span></div>`).join('') + `</div>`;
  if ((el.opacity ?? 1) < 1) html += `<p class="note">See-through pieces let the drink show through them.</p>`;
  const d = dpiOf(el);
  if (d < 150) html += `<p class="note" style="color:var(--lava)">Only ${Math.round(d)} DPI at this size, so it may print soft. Make it smaller, or use a larger image.</p>`;
  html += `<div class="btnrow">
    ${el.kind === 'art' || el.kind === 'upload' ? `<button type="button" class="sbtn" id="ix-flip">Flip</button>` : ''}
    <button type="button" class="sbtn" id="ix-back">Send back</button>
    <button type="button" class="sbtn" id="ix-fwd">Bring forward</button>
    <button type="button" class="sbtn" id="ix-dup">Copy</button>
    <button type="button" class="sbtn sbtn--lava" id="ix-del">Remove</button></div>`;
  insp.innerHTML = html;

  sliders.forEach(s => {
    const inp = $('#ix-' + s.key), out = $('#ix-' + s.key + '-v');
    inp.addEventListener('input', () => { s.onInput(+inp.value); fit(el); out.textContent = s.fmt(+inp.value); drawBand(); requestTexture(); });
    inp.addEventListener('change', () => commit());
  });
  $('#ix-done').onclick = () => select(null);
  const fl = $('#ix-flip'); if (fl) fl.onclick = () => { el.flipX = !el.flipX; commit(); };
  $('#ix-back').onclick = () => moveLayer(el, -1);
  $('#ix-fwd').onclick = () => moveLayer(el, 1);
  $('#ix-dup').onclick = () => duplicate(el);
  $('#ix-del').onclick = () => removeEl(el);
  insp.querySelectorAll('[data-color]').forEach(b => b.onclick = () => { el.color = b.dataset.color || null; commit(); });
  const anyIn = $('#ix-any'), hexIn = $('#ix-hex');
  if (anyIn) {
    // live while the picker is open; one undo step when it closes
    const live = v => {
      el.color = v.toLowerCase();
      const sw = insp.querySelector('.sw--any');
      sw.style.background = el.color; sw.setAttribute('aria-pressed', 'true');
      insp.querySelectorAll('.sw[data-color]').forEach(b => b.setAttribute('aria-pressed', 'false'));
      drawBand(); requestTexture();
    };
    anyIn.addEventListener('input', () => { live(anyIn.value); hexIn.value = el.color; });
    anyIn.addEventListener('change', () => commit());
    hexIn.addEventListener('input', () => {
      let v = hexIn.value.trim();
      if (v && v[0] !== '#') v = '#' + v;
      if (HEX_RE.test(v)) {                     // a complete code is its own undo step
        live(v); anyIn.value = el.color; commit();
        const h2 = $('#ix-hex');                // the panel was rebuilt: keep typing where they were
        if (h2) { h2.focus(); h2.setSelectionRange(h2.value.length, h2.value.length); }
      }
    });
    hexIn.addEventListener('change', () => { if (HEX_RE.test(el.color || '')) { hexIn.value = el.color; commit(); } else hexIn.value = hexOf(el.color); });
  }
  if (el.kind === 'text') {
    const ta = $('#ix-text');
    ta.addEventListener('input', async () => {
      const clean = cleanText(ta.value);
      if (clean !== ta.value) { const pos = ta.selectionStart; ta.value = clean; ta.selectionStart = ta.selectionEnd = Math.min(pos, clean.length); }
      el.text = clean || ' ';
      await ensureFont(el.font, el.text);
      fit(el); drawBand(); requestTexture();
      $('.insp__title').textContent = elName(el);
    });
    ta.addEventListener('change', () => commit());
    insp.querySelectorAll('[data-font]').forEach(b => b.onclick = async () => { el.font = b.dataset.font; await ensureFont(el.font, el.text); fit(el); commit(); });
    insp.querySelectorAll('[data-align]').forEach(b => b.onclick = () => { el.align = b.dataset.align; commit(); });
  }
}
// slider values follow a drag on the band without rebuilding the panel
function syncInspector() {
  const el = byUid(selected);
  if (!el) return;
  const set = (k, v, txt) => { const i = $('#ix-' + k); if (i) { i.value = v; $('#ix-' + k + '-v').textContent = txt; } };
  if (el.w !== undefined && (el.kind === 'art' || el.kind === 'upload')) set('size', el.w, Math.round(el.w) + ' mm');
  if (el.kind === 'text') set('tsize', el.size, Math.round(el.size) + ' mm');
  if (el.rot !== undefined) set('rot', el.rot, el.rot + '°');
  set('x', el.kind === 'pattern' ? mod(el.dx || 0, W) : el.x, Math.round(el.kind === 'pattern' ? mod(el.dx || 0, W) : el.x) + ' mm');
  set('y', el.y, Math.round(el.y) + ' mm');
}
function cleanText(v) {
  return v.replace(/\r/g, '').split('\n').slice(0, LIMITS.lines).map(l => l.slice(0, LIMITS.chars)).join('\n');
}
function esc(s) { return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

// =====================================================================================
// library panel
// =====================================================================================
const TABS = [
  { id: 'illus', name: 'Illustrations', cats: ['Illustrations', 'Island'] },
  { id: 'trio', name: 'Tiki Trio', cats: ['Tiki Trio'] },
  { id: 'logos', name: 'Logos', cats: ['Logos'] },
  { id: 'patterns', name: 'Patterns', cats: ['Patterns'] },
  { id: 'borders', name: 'Borders' },
  { id: 'text', name: 'Text' },
  { id: 'upload', name: 'Your image' }
];
let tab = 'illus';
function renderTabs() {
  const t = $('#tabs');
  t.innerHTML = TABS.map(x => `<button type="button" class="tab" role="tab" id="tab-${x.id}" aria-controls="libPanel" aria-selected="${x.id === tab}" data-tab="${x.id}">${x.name}</button>`).join('');
  t.querySelectorAll('[data-tab]').forEach(b => b.onclick = () => { tab = b.dataset.tab; renderTabs(); renderLib(); });
  $('#libPanel').setAttribute('aria-labelledby', 'tab-' + tab);
}
function renderLib() {
  const p = $('#libPanel'), T = TABS.find(x => x.id === tab);
  if (T.id === 'borders') {
    p.innerHTML = `<div class="lib">${BORDERS.map(b => `<button type="button" class="item" data-border="${b.id}"><canvas width="200" height="120"></canvas><span>${b.name}</span></button>`).join('')}</div>`;
    p.querySelectorAll('[data-border]').forEach(btn => {
      const b = borderOf(btn.dataset.border), c = btn.querySelector('canvas'), x = c.getContext('2d'), s = 200 / 60;
      x.setTransform(s, 0, 0, s, 0, 0);
      drawBorder(x, { id: b.id, y: 18, h: Math.min(b.h * 1.4, 14), color: 'paper' }, { W: 60, x0: 0, x1: 60 }, 1);
      btn.onclick = () => addBorder(b);
    });
    return;
  }
  if (T.id === 'text') {
    p.innerHTML = `<div class="lib"><button type="button" class="item item--text" id="addText"><b style="font-family:Chicle">Aa</b><span>Add a line of text</span></button></div>
      <p class="note">A name, a bar, a date: up to ${LIMITS.lines} lines of ${LIMITS.chars} letters, in eight house faces.</p>`;
    $('#addText').onclick = addText;
    return;
  }
  if (T.id === 'upload') {
    p.innerHTML = `<div class="uploadbox"><label class="label" for="upFile" style="margin:0">Add your own image</label>
      <input type="file" id="upFile" accept="image/png,image/jpeg,image/webp">
      <p class="note">PNG with a clear background works best. You can place it among our art, size it and turn it.
      Only use images you have the right to print.</p></div>`;
    $('#upFile').onchange = e => { addUpload(e.target.files[0]); e.target.value = ''; };
    return;
  }
  const items = lib.items.filter(i => T.cats.includes(i.category));
  if (!items.length) {
    p.innerHTML = `<div class="lib"><p class="libmsg">${lib.ok ? 'Nothing on this shelf yet.' : 'The library is being stocked. Borders, text and your own images work now.'}</p></div>`;
    return;
  }
  p.innerHTML = `<div class="lib">${items.map(i => `<button type="button" class="item" data-item="${esc(i.id)}"><img alt="" loading="lazy" decoding="async" src="${esc(libUrl(i.thumb || i.preview))}"><span>${esc(i.name)}</span></button>`).join('')}</div>`;
  p.querySelectorAll('[data-item]').forEach(b => b.onclick = () => addLibrary(lib.byId.get(b.dataset.item)));
}

// =====================================================================================
// glass switching
// =====================================================================================
function setGlass(key, keepDesign = true) {
  const old = G;
  glassKey = key; G = GLASSES[key];
  const sx = G.print.W / old.print.W, sy = G.print.H / old.print.H;
  W = G.print.W; H = G.print.H;
  document.querySelectorAll('.chip[data-glass]').forEach(c => c.setAttribute('aria-checked', String(c.dataset.glass === key)));
  $('#bandLabel').textContent = `${G.short} band · ${W} × ${H} mm · wraps all the way around`;
  if (keepDesign && old !== G) elements.forEach(el => {
    if (el.x !== undefined) el.x *= sx;
    if (el.dx !== undefined) el.dx *= sx;
    el.y *= sy;
    fit(el);
  });
  layoutBand();
  rebuildGlass();
  refreshPrice();
  if (keepDesign && old !== G) commit();
}
document.querySelectorAll('.chip[data-glass]').forEach(c => c.onclick = () => { if (c.dataset.glass !== glassKey) setGlass(c.dataset.glass); });

// =====================================================================================
// 3D preview: the live page's lathe glass with the band texture drawn by drawScene()
// =====================================================================================
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const T3 = { ok: false };
function init3D() {
  const viewer = $('#viewer');
  let renderer;
  try {
    // preserveDrawingBuffer: the order's preview snapshot reads back the rendered frame
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  } catch (e) {
    $('#viewerHint').textContent = 'The 3D preview needs a newer browser. The flat band below is exact.';
    return;
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  viewer.prepend(renderer.domElement);
  const scene = new THREE.Scene();
  scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;
  const camera = new THREE.PerspectiveCamera(34, 1, 1, 2000);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enablePan = false;
  controls.enableDamping = true;
  controls.minDistance = 140;
  controls.maxDistance = 560;
  scene.add(new THREE.AmbientLight(0xf1e3bf, 0.55));
  const key = new THREE.DirectionalLight(0xffffff, 1.4); key.position.set(120, 220, 160); scene.add(key);
  const rim = new THREE.DirectionalLight(0xc9a227, 0.5); rim.position.set(-150, 90, -120); scene.add(rim);
  const glassMat = new THREE.MeshPhysicalMaterial({ transmission: 1, thickness: 2.2, roughness: 0.05, ior: 1.5,
    clearcoat: 1, transparent: true, color: 0xffffff });
  const texCanvas = document.createElement('canvas');
  const tex = new THREE.CanvasTexture(texCanvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  const bandMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide, depthWrite: false });
  const group = new THREE.Group();
  scene.add(group);
  Object.assign(T3, { ok: true, renderer, scene, camera, controls, group, glassMat, bandMat, tex, texCanvas,
                      glass: null, band: null, rotTarget: Math.PI, userTurned: false });
  group.rotation.y = Math.PI;               // the middle of the design faces you; the seam is at the back

  renderer.domElement.addEventListener('pointerdown', () => { T3.userTurned = true; });
  function resize() {
    const w = viewer.clientWidth, h = viewer.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(viewer);
  T3.resize = resize;
  (function tick() {
    requestAnimationFrame(tick);
    if (!viewer.clientHeight) return;
    if (step === 'review' && !T3.userTurned && !REDUCED) T3.rotTarget += 0.004;
    const d = T3.rotTarget - group.rotation.y;
    group.rotation.y += REDUCED ? d : d * 0.12;
    controls.update();
    renderer.render(scene, camera);
  })();
  rebuildGlass();
  resize();
}
function rebuildGlass() {
  if (!T3.ok) return;
  const { group, glassMat, bandMat, camera, controls } = T3;
  if (T3.glass) { group.remove(T3.glass); T3.glass.geometry.dispose(); }
  if (T3.band) { group.remove(T3.band); T3.band.geometry.dispose(); }
  const midR = y => G.botR + (G.topR - G.botR) * (y / G.H);
  const profile = [
    [0, 0], [G.botR * 0.62, 0], [G.botR - 0.6, 0], [G.botR, 1.6], [G.botR + 0.2, 6],
    [midR(G.H * 0.26), G.H * 0.26], [midR(G.H * 0.52), G.H * 0.52], [midR(G.H * 0.78), G.H * 0.78],
    [G.topR, G.H - 1], [G.topR - 0.4, G.H], [G.topR - 1.6, G.H - 0.6]
  ].map(([r, h]) => new THREE.Vector2(r, h));
  T3.glass = new THREE.Mesh(new THREE.LatheGeometry(profile, 128), glassMat);
  group.add(T3.glass);
  const pts = [];
  for (let i = 0; i <= 16; i++) { const y = G.print.yBot + (G.print.yTop - G.print.yBot) * i / 16; pts.push(new THREE.Vector2(midR(y) + 0.25, y)); }
  T3.band = new THREE.Mesh(new THREE.LatheGeometry(pts, 128), bandMat);
  T3.band.renderOrder = 1;
  group.add(T3.band);
  const dist = G.H * 2.3 + 60;
  camera.position.set(0, G.H * 0.62 + 40, dist);
  controls.target.set(0, G.H * 0.47, 0);
  T3.texCanvas.width = 2048;
  T3.texCanvas.height = Math.round(2048 * H / W);
  T3.tex.dispose();          // new size → new GPU texture
  requestTexture();
}
let texPending = false;
function requestTexture() {
  if (!T3.ok || texPending) return;
  texPending = true;
  requestAnimationFrame(() => { texPending = false; drawTexture(); });
}
function drawTexture() {
  const c = T3.texCanvas, x = c.getContext('2d'), s = c.width / W;
  x.setTransform(1, 0, 0, 1, 0, 0);
  x.clearRect(0, 0, c.width, c.height);
  x.setTransform(s, 0, 0, c.height / H, 0, 0);
  drawScene(x, { W, x0: 0, x1: W, src: previewSource });
  if (step === 'design') {                  // a faint seam line at the back of the glass
    x.setTransform(1, 0, 0, 1, 0, 0);
    x.fillStyle = 'rgba(200,69,42,.55)';
    for (let yy = 0; yy < c.height; yy += 16) { x.fillRect(0, yy, 3, 9); x.fillRect(c.width - 3, yy, 3, 9); }
  }
  T3.tex.needsUpdate = true;
}
// turn the glass so mm position x faces the viewer
function faceX(x) {
  if (!T3.ok) return;
  let t = -2 * Math.PI * (x / W);           // band u = x/W sits at angle 2πu; turn it to the front
  const cur = T3.group.rotation.y;
  t += Math.round((cur - t) / (2 * Math.PI)) * 2 * Math.PI;
  T3.rotTarget = t;
}
// 1200 × 900 JPEG of the glass, for the order's _Design Preview
function previewDataURL() {
  if (!T3.ok) return null;
  const { renderer, camera, scene } = T3;
  const prevAspect = camera.aspect, size = renderer.getSize(new THREE.Vector2());
  const pr = renderer.getPixelRatio();
  renderer.setPixelRatio(1);
  renderer.setSize(1200, 900, false);
  camera.aspect = 1200 / 900; camera.updateProjectionMatrix();
  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL('image/jpeg', 0.85);
  renderer.setPixelRatio(pr);
  renderer.setSize(size.x, size.y, false);
  camera.aspect = prevAspect; camera.updateProjectionMatrix();
  return url;
}

// =====================================================================================
// design JSON (_Design)
// =====================================================================================
function designJSON(dpi, omitted = [], uploadKeys = null) {
  const keys = uploadKeys || uploadKeyMap();
  return {
    v: 1, glass: glassKey, dpi, mm: [W, H], omitted,
    elements: elements.map(el => {
      const o = { kind: el.kind };
      if (el.id) o.id = el.id;
      if (el.kind === 'upload') o.upload = keys.get(el.upload) || null;
      for (const k of ['x', 'y', 'w', 'h', 'tile', 'size']) if (el[k] !== undefined) o[k] = r1(el[k]);
      if (el.kind === 'pattern') { o.rows = el.rows || 1; o.dx = r1(el.dx || 0); }
      if (el.rot) o.rot = el.rot;
      if (el.flipX) o.flipX = true;
      if (el.color) o.color = el.color;
      if ((el.opacity ?? 1) < 1) o.opacity = el.opacity;
      if (el.kind === 'text') { o.text = el.text; o.font = el.font; o.align = el.align; }
      return o;
    })
  };
}
// upload key → '_Upload n', in the order they appear in the design
function uploadKeyMap() {
  const m = new Map();
  elements.forEach(el => { if (el.kind === 'upload' && !m.has(el.upload)) m.set(el.upload, '_Upload ' + (m.size + 1)); });
  return m;
}

// =====================================================================================
// price, steps, order (cart side: snapshot 3)
// =====================================================================================
let qty = 1, cartQty = 0;
const money = v => '$' + v.toFixed(2);
function shopGlass() { return SHOP ? SHOP.glasses && SHOP.glasses[glassKey] : null; }
function basePrice() { const g = shopGlass(); return g ? g.price : (SHOP ? null : DEV_PRICE); }
function tiers() { return ((SHOP ? SHOP.breaks : DEV_BREAKS) || []).slice().sort((a, b) => a.min - b.min); }
function eachPrice(n) { let p = basePrice(); tiers().forEach(t => { if (n >= t.min) p = Math.min(p, t.price); }); return p; }
async function refreshPrice() {
  cartQty = 0;
  if (SHOP && SHOP.cartQty) { try { cartQty = (await SHOP.cartQty(glassKey)) || 0; } catch (e) { cartQty = 0; } }
  renderPrice();
}
function renderPrice() {
  const base = basePrice();
  if (base == null) {
    $('#barPrice').textContent = '—';
    $('#barNote').textContent = 'resting between batches';
    $('#price').textContent = '—';
    $('#priceNote').textContent = 'This glass is resting between batches. Design freely; ordering reopens soon.';
    $('#breaks').hidden = true; $('#nudge').hidden = true;
    updateAtc();
    return;
  }
  const total = cartQty + qty, each = eachPrice(total), ts = tiers();
  $('#barPrice').textContent = money(each);
  $('#barNote').textContent = ts.length ? `a glass · ${ts[0].min}+ for ${money(ts[0].price)} each` : 'a glass';
  $('#price').textContent = money(each * qty);
  let note = qty + ' × ' + money(each) + ' each';
  if (each < base) note += ' · you save ' + money((base - each) * qty);
  if (cartQty > 0) note += ' · counting the ' + cartQty + ' already in your cart';
  $('#priceNote').textContent = note;
  const br = $('#breaks');
  br.innerHTML = '';
  if (ts.length) {
    const rows = [{ min: 1, price: base }].concat(ts);
    rows.forEach((t, i) => {
      const next = rows[i + 1], li = document.createElement('li');
      if (total >= t.min && (!next || total < next.min)) li.className = 'on';
      const range = next ? (t.min === next.min - 1 ? t.min : t.min + '–' + (next.min - 1)) + (next.min - 1 === 1 ? ' glass' : ' glasses') : t.min + ' or more';
      li.innerHTML = `<span>${range}</span><span>${money(t.price)} each</span>`;
      br.appendChild(li);
    });
  }
  br.hidden = !ts.length;
  const up = ts.find(t => t.min > total);
  $('#nudge').hidden = !up;
  if (up) $('#nudge').textContent = `Add ${up.min - total} more and every glass drops to ${money(up.price)}.`;
  updateAtc();
}
function setQty(n) { n = Math.floor(+n); qty = clamp(isFinite(n) ? n : 1, 1, 500); $('#qty').value = qty; renderPrice(); }
$('#qtyMinus').onclick = () => setQty(qty - 1);
$('#qtyPlus').onclick = () => setQty(qty + 1);
$('#qty').onchange = () => setQty($('#qty').value);

let step = 'design';
function goStep(s) {
  step = s;
  $('#designPanel').hidden = s !== 'design';
  $('#reviewPanel').hidden = s !== 'review';
  $('#obar').hidden = s === 'review';
  document.body.classList.toggle('is-review', s === 'review');
  $('#band').tabIndex = s === 'review' ? -1 : 0;
  T3.userTurned = false;
  if (s === 'review') { select(null); renderReview(); $('#reviewH').focus?.(); window.scrollTo({ top: 0, behavior: 'auto' }); }
  requestTexture();
}
$('#nextBtn').onclick = () => {
  if (!elements.length) return say('Your band is empty. Add a piece of art, a pattern or some words first.', true);
  goStep('review');
};
$('#backBtn').onclick = () => goStep('design');
$('#reviewH').tabIndex = -1;

function renderReview() {
  const kinds = { art: 0, pattern: 0, border: 0, text: 0, upload: 0 };
  elements.forEach(e => kinds[e.kind]++);
  const parts = [];
  if (kinds.art) parts.push(kinds.art + ' art');
  if (kinds.pattern) parts.push(kinds.pattern + (kinds.pattern > 1 ? ' patterns' : ' pattern'));
  if (kinds.border) parts.push(kinds.border + (kinds.border > 1 ? ' borders' : ' border'));
  if (kinds.text) parts.push(kinds.text + ' text');
  if (kinds.upload) parts.push(kinds.upload + (kinds.upload > 1 ? ' of your images' : ' of your image'));
  $('#summary').innerHTML = `<li><span>Glass</span><span>${G.label}</span></li>
    <li><span>Print band</span><span>${W} × ${H} mm, all the way around</span></li>
    <li><span>On it</span><span>${parts.join(', ')}</span></li>`;
  const w = [];
  elements.forEach(e => { const d = dpiOf(e); if (d < 150) w.push(`${elName(e)} is only ${Math.round(d)} DPI at its size, so it may print soft.`); });
  if (elements.some(e => (e.opacity ?? 1) < 1)) w.push('Some pieces are see-through, so the drink will show through them.');
  if (elements.some(e => { const hx = e.kind === 'pattern' || e.kind === 'border' ? 0 : halfX(e); return e.x !== undefined && (e.x - hx < 0 || e.x + hx > W); }))
    w.push('A piece crosses the seam at the back of the glass. It joins up there, as the preview shows.');
  $('#warns').innerHTML = w.map(x => `<li>${esc(x)}</li>`).join('');
  $('#warns').hidden = !w.length;
  renderPrice();
}

$('#ack').onchange = updateAtc;
function updateAtc() {
  $('#atc').disabled = busy || !($('#ack').checked && elements.length && basePrice() != null && (DEV || shopGlass()));
}

// =====================================================================================
// print file: rendered in horizontal strips at real size and streamed into a PNG, so no
// canvas ever holds the whole file (iOS caps one canvas at 16.7 MP)
// =====================================================================================
const CRC_T = new Uint32Array(256).map((_, n) => { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; return c >>> 0; });
function crc32(parts) {
  let c = 0xffffffff;
  for (const b of parts) for (let i = 0; i < b.length; i++) c = CRC_T[(c ^ b[i]) & 255] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
function pngChunk(type, data) {
  const head = new Uint8Array(8), crc = new Uint8Array(4);
  new DataView(head.buffer).setUint32(0, data.length);
  for (let i = 0; i < 4; i++) head[4 + i] = type.charCodeAt(i);
  new DataView(crc.buffer).setUint32(0, crc32([head.subarray(4), data]));
  return [head, data, crc];
}
// mode 'rgba': 8-bit RGBA, adaptive row filters. mode 'mask': 1-bit palette, index 1 = black, 0 = clear.
class PNGStream {
  constructor(w, h, mode, dpi) {
    Object.assign(this, { w, h, mode, dpi });
    this.rb = mode === 'rgba' ? w * 4 : Math.ceil(w / 8);
    this.prev = new Uint8Array(this.rb);
    this.cur = new Uint8Array(this.rb);
    this.cand = [0, 1, 2, 3, 4].map(() => new Uint8Array(this.rb));
    this.cs = new CompressionStream('deflate');          // zlib stream = PNG's IDAT format
    this.writer = this.cs.writable.getWriter();
    this.out = new Response(this.cs.readable).arrayBuffer();
  }
  async addRows(rgba, rows) {
    const { rb, w } = this, buf = new Uint8Array((rb + 1) * rows);
    for (let r = 0; r < rows; r++) {
      const line = this.cur;
      if (this.mode === 'rgba') line.set(rgba.subarray(r * w * 4, (r + 1) * w * 4));
      else {
        line.fill(0);
        for (let x = 0, i = r * w * 4 + 3; x < w; x++, i += 4) if (rgba[i] >= 128) line[x >> 3] |= 0x80 >> (x & 7);
      }
      let ft = 0, best = line;
      if (this.mode === 'rgba') {
        const prev = this.prev, [, sub, up, , pae] = this.cand;
        let sN = 0, sS = 0, sU = 0, sP = 0;
        for (let i = 0; i < rb; i++) {
          const a = i >= 4 ? line[i - 4] : 0, b = prev[i], c = i >= 4 ? prev[i - 4] : 0, x = line[i];
          const p = a + b - c, pa = Math.abs(p - a), pb = Math.abs(p - b), pc = Math.abs(p - c);
          const pr = pa <= pb && pa <= pc ? a : pb <= pc ? b : c;
          const vs = (x - a) & 255, vu = (x - b) & 255, vp = (x - pr) & 255;
          sub[i] = vs; up[i] = vu; pae[i] = vp;
          sN += x < 128 ? x : 256 - x; sS += vs < 128 ? vs : 256 - vs; sU += vu < 128 ? vu : 256 - vu; sP += vp < 128 ? vp : 256 - vp;
        }
        const m = Math.min(sN, sS, sU, sP);
        if (m === sP) { ft = 4; best = pae; } else if (m === sU) { ft = 2; best = up; } else if (m === sS) { ft = 1; best = sub; }
      }
      const o = r * (rb + 1);
      buf[o] = ft;
      buf.set(best, o + 1);
      [this.prev, this.cur] = [this.cur, this.prev];
    }
    await this.writer.write(buf);
  }
  async finish() {
    await this.writer.close();
    const idat = new Uint8Array(await this.out);
    const ihdr = new Uint8Array(13), dv = new DataView(ihdr.buffer);
    dv.setUint32(0, this.w); dv.setUint32(4, this.h);
    ihdr[8] = this.mode === 'rgba' ? 8 : 1;
    ihdr[9] = this.mode === 'rgba' ? 6 : 3;
    const phys = new Uint8Array(9), pv = new DataView(phys.buffer), ppm = Math.round(this.dpi / 0.0254);
    pv.setUint32(0, ppm); pv.setUint32(4, ppm); phys[8] = 1;
    const parts = [new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]), ...pngChunk('IHDR', ihdr), ...pngChunk('pHYs', phys)];
    if (this.mode === 'mask') parts.push(...pngChunk('PLTE', new Uint8Array([0, 0, 0, 0, 0, 0])), ...pngChunk('tRNS', new Uint8Array([0, 255])));
    const CH = 1 << 22;
    for (let i = 0; i < idat.length; i += CH) parts.push(...pngChunk('IDAT', idat.subarray(i, Math.min(idat.length, i + CH))));
    parts.push(...pngChunk('IEND', new Uint8Array(0)));
    return new Blob(parts, { type: 'image/png' });
  }
}

// print-resolution sources, made at exactly the pixel size they will be drawn at
const svgTexts = new Map(), masterImgs = new Map();
function masterImg(url) { if (!masterImgs.has(url)) masterImgs.set(url, loadImage(url)); return masterImgs.get(url); }
async function svgAt(url, wpx, hpx) {
  if (!svgTexts.has(url)) svgTexts.set(url, fetch(url).then(r => { if (!r.ok) throw new Error('svg ' + r.status); return r.text(); }));
  const svg = new DOMParser().parseFromString(await svgTexts.get(url), 'image/svg+xml').documentElement;
  svg.setAttribute('width', wpx); svg.setAttribute('height', hpx); svg.setAttribute('preserveAspectRatio', 'none');
  const u = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' }));
  try { return await loadImage(u); } finally { URL.revokeObjectURL(u); }
}
async function printRaster(el, wpx, hpx) {
  wpx = Math.max(1, Math.ceil(wpx)); hpx = Math.max(1, Math.ceil(hpx));
  let src, tint = null;
  if (el.kind === 'upload') src = uploads.get(el.upload).img;
  else {
    const item = lib.byId.get(el.id), url = libUrl(item.master || item.preview);
    src = /\.svg(\?|$)/i.test(url) ? await svgAt(url, wpx, hpx) : await masterImg(url);
    if (item.recolor && el.color) tint = hexOf(el.color);
  }
  const c = document.createElement('canvas');
  c.width = wpx; c.height = hpx;
  const x = c.getContext('2d');
  x.imageSmoothingQuality = 'high';
  x.drawImage(src, 0, 0, wpx, hpx);
  if (tint) { x.globalCompositeOperation = 'source-in'; x.fillStyle = tint; x.fillRect(0, 0, wpx, hpx); }
  return c;
}
// yield to the page between strips (a message, not a timer: timers are throttled in background tabs)
const tick0 = () => new Promise(r => { const ch = new MessageChannel(); ch.port1.onmessage = () => r(); ch.port2.postMessage(0); });
function vExtent(e) { return e.kind === 'pattern' ? patternGeom(e, W).h / 2 : e.kind === 'border' ? e.h / 2 : halfY(e); }

// one print file (or the white layer, mask = true) at `dpi`
async function renderPrint(dpi, mask, onProgress) {
  const Wpx = Math.round(W / 25.4 * dpi), Hpx = Math.round(H / 25.4 * dpi);
  const sx = Wpx / W, sy = Hpx / H;          // W mm is exactly Wpx, so the seam lines up to the pixel
  const list = mask ? elements.filter(e => (e.opacity ?? 1) >= 1) : elements;
  const stripH = clamp(Math.floor(4e6 / Wpx), 16, Hpx);
  const strip = document.createElement('canvas');
  strip.width = Wpx; strip.height = stripH;
  const ctx = strip.getContext('2d', { willReadFrequently: true });
  const png = new PNGStream(Wpx, Hpx, mask ? 'mask' : 'rgba', dpi);
  const cache = new Map();
  for (let top = 0; top < Hpx; top += stripH) {
    const h = Math.min(stripH, Hpx - top), y0 = top / sy, y1 = (top + h) / sy;
    const here = list.filter(e => e.y + vExtent(e) >= y0 && e.y - vExtent(e) <= y1);
    for (const uid of [...cache.keys()]) if (!here.some(e => e.uid === uid)) cache.delete(uid);
    for (const e of here) {
      if (cache.has(e.uid) || e.kind === 'text' || e.kind === 'border') continue;
      if (e.kind === 'pattern') { const g = patternGeom(e, W); cache.set(e.uid, await printRaster(e, g.tw * sx + 1, g.th * sy + 1)); }
      else cache.set(e.uid, await printRaster(e, e.w * sx, e.h * sy));
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, Wpx, stripH);
    ctx.setTransform(sx, 0, 0, sy, 0, -top);
    drawScene(ctx, { W, x0: 0, x1: W, list: here, src: e => cache.get(e.uid), alphaOne: mask });
    await png.addRows(ctx.getImageData(0, 0, Wpx, h).data, h);
    if (onProgress) onProgress((top + h) / Hpx);
    await tick0();
  }
  return { blob: await png.finish(), Wpx, Hpx };
}

// the customer's own images, resampled to what they need at their placed size at 600 DPI
async function resampleUploads(keys) {
  const out = [];
  for (const [ukey, prop] of keys) {
    const u = uploads.get(ukey);
    const maxW = Math.max(...elements.filter(e => e.upload === ukey).map(e => e.w));
    const s = Math.min(1, (maxW / 25.4 * 600) / u.w);
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(u.w * s)); c.height = Math.max(1, Math.round(u.h * s));
    const x = c.getContext('2d'); x.imageSmoothingQuality = 'high'; x.drawImage(u.img, 0, 0, c.width, c.height);
    const jpg = u.file.type === 'image/jpeg';
    const blob = await new Promise(r => c.toBlob(r, jpg ? 'image/jpeg' : 'image/png', 0.9));
    out.push({ key: prop, blob, filename: prop.replace('_Upload ', 'upload-') + (jpg ? '.jpg' : '.png') });
  }
  return out;
}
function flatPreviewDataURL() {
  const c = document.createElement('canvas'); c.width = 1200; c.height = 900;
  const x = c.getContext('2d'); x.fillStyle = '#0e3f42'; x.fillRect(0, 0, 1200, 900);
  const s = 1120 / W; x.setTransform(s, 0, 0, s, 40, (900 - H * s) / 2);
  drawScene(x, { W, x0: 0, x1: W, src: previewSource });
  return c.toDataURL('image/jpeg', 0.85);
}

// everything the order carries, stepped down until the request fits the budget
async function buildPrintSet(progress) {
  if (typeof CompressionStream === 'undefined') throw Object.assign(new Error('Your browser is too old to send a design. Please update it and try again.'), { shown: true });
  const keys = uploadKeyMap();
  let ups = await resampleUploads(keys), omitted = [];
  const preview = previewDataURL() || flatPreviewDataURL();
  const fixed = Math.round(preview.length * 0.75) + 32 * 1024;      // preview + form fields + _Design
  const seeThrough = elements.some(e => (e.opacity ?? 1) < 1);
  const t0 = performance.now();
  let lastArt = null;
  for (const [i, dpi] of PRINT_DPI.entries()) {
    const span = 1 / PRINT_DPI.length, base = i * span, parts = seeThrough ? 2 : 1;
    // skip a step that can't fit: file size scales roughly with pixel count
    if (lastArt && i < PRINT_DPI.length - 1 && lastArt.size * (dpi / lastArt.dpi) ** 2 + fixed > BUDGET * 1.15) continue;
    const art = await renderPrint(dpi, false, f => progress(base + span * f / parts));
    const white = seeThrough ? await renderPrint(dpi, true, f => progress(base + span * (0.5 + f / 2))) : null;
    lastArt = { size: art.blob.size, dpi };
    const upBytes = () => ups.reduce((n, u) => n + u.blob.size, 0);
    let total = art.blob.size + (white ? white.blob.size : 0) + fixed + upBytes();
    if (total > BUDGET && ups.length) {        // first: leave out the originals (Artwork is what prints)
      omitted = ups.map(u => u.key); ups = [];
      total = art.blob.size + (white ? white.blob.size : 0) + fixed;
    }
    if (total <= BUDGET) {
      progress(1);
      return { dpi, art, white, ups, omitted, keys, preview, total, ms: Math.round(performance.now() - t0) };
    }
  }
  throw Object.assign(new Error('Your design is too heavy to send in one piece. Try a smaller photo, or fewer of your own images.'), { shown: true });
}

let busy = false;
function setProgress(f) { const p = $('#progress'); p.hidden = f == null; if (f != null) p.firstElementChild.style.width = Math.round(f * 100) + '%'; }
function setStatus(msg, err) { const s = $('#status'); s.textContent = msg; s.classList.toggle('status--err', !!err); }
$('#atc').onclick = async () => {
  if (busy || $('#atc').disabled) return;
  busy = true;
  $('#atc').disabled = true; $('#backBtn').disabled = true;
  const version = designVersion;
  try {
    setStatus('Setting your design in place…');
    setProgress(0);
    const set = await buildPrintSet(f => { setProgress(f * (SHOP ? 0.5 : 1)); setStatus('Setting your design in place… ' + Math.round(f * 100) + '%'); });
    if (version !== designVersion) throw Object.assign(new Error('Your design changed. Please check it and tick the box again.'), { shown: true });
    const design = designJSON(set.dpi, set.omitted, set.keys);
    const images = { 'Artwork': { blob: set.art.blob, filename: `glass-art-${glassKey}-${set.dpi}dpi.png` } };
    if (set.white) images['_White Layer'] = { blob: set.white.blob, filename: `white-layer-${glassKey}-${set.dpi}dpi.png` };
    set.ups.forEach(u => { images[u.key] = { blob: u.blob, filename: u.filename }; });
    const order = {
      glass: glassKey, quantity: qty, unitPrice: basePrice(), preview: set.preview,
      properties: {
        'Glass': (shopGlass() && shopGlass().label) || G.label,
        'Placement': 'designed from the library',
        '_config_id': 'GAT-' + Date.now().toString(36),
        '_Artwork final': 'Customer agreed the artwork is final and prints as submitted ' + new Date().toISOString(),
        '_Design': JSON.stringify(design)
      }
    };
    if (DEV) { devReport(order, images, set); setStatus(`Dev: print files made at ${set.dpi} DPI in ${set.ms} ms. No cart in dev mode.`); return; }
    setStatus('Uploading your design… 0%');
    await SHOP.addToCart(order, images, f => { setProgress(0.5 + f * 0.5); setStatus('Uploading your design… ' + Math.round(f * 100) + '%'); });
    setProgress(1);
    setStatus('In the cart. Your design is pinned to the order.');
    try { localStorage.removeItem(SAVE_KEY); } catch (e) {}
  } catch (e) {
    console.error(e);
    setStatus(e && e.shown && e.message ? e.message : 'The cart balked. Please try once more.', true);
    setProgress(null);
  } finally {
    busy = false;
    $('#backBtn').disabled = false;
    updateAtc();
  }
};

// =====================================================================================
// small things
// =====================================================================================
// short messages: read out on the band, and shown on the current step (returns false for canAdd)
let sayTimer;
function say(msg) {
  $('#bandLive').textContent = msg;
  const s = step === 'review' ? $('#status') : $('#designMsg');
  s.textContent = msg;
  clearTimeout(sayTimer);
  if (step !== 'review') sayTimer = setTimeout(() => { s.textContent = ''; }, 6000);
  return false;
}
$('#undoBtn').onclick = undo;
$('#redoBtn').onclick = redo;
$('#zoomIn').onclick = () => { view.zoom = Math.min(4, view.zoom * 1.5); layoutBand(); };
$('#zoomOut').onclick = () => { view.zoom = Math.max(1, view.zoom / 1.5); layoutBand(); };
$('#bgBtn').onclick = () => { view.bg = (view.bg + 1) % BACKDROPS.length; drawBand(); say('Backdrop: ' + BACKDROPS[view.bg].name); };
$('#lookBtn').onclick = () => {
  const st = $('#stage'), off = st.classList.toggle('is-nolook');
  $('#lookBtn').setAttribute('aria-pressed', String(!off));
  layoutBand();
};
new ResizeObserver(() => layoutBand()).observe($('#bandScroll'));
const alt = $('#altLink');
alt.href = '/pages/glassware-foundry' + (glassKey ? '?glass=' + glassKey : '');

// =====================================================================================
// dev mode
// =====================================================================================
if (DEV) {
  window.GAT = { get elements() { return elements; }, lib, uploads, view, T3, renderPrint, designJSON, commit, setGlass, addUpload };   // console access for testing
  $('#dev').hidden = false;
  $('#atc').textContent = 'Make print files (dev)';
  $('#devButtons').innerHTML = `<button type="button" id="devClear">clear design</button><button type="button" id="devDemo">demo design</button>`;
  $('#devClear').onclick = () => { elements = []; selected = null; commit(); };
  $('#devDemo').onclick = async () => {
    elements = []; selected = null;
    const get = id => lib.byId.get(id);
    for (const it of ['tapa-1-v1', 'trio-detailed-brown-v1', 'tiki-sipping-alt-v1'].map(get).filter(Boolean)) it._img = await previewOf(it);
    if (get('tapa-1-v1')) elements.push({ uid: uidSeq++, kind: 'pattern', id: 'tapa-1-v1', y: SAFE + 5, tile: 10, rows: 1, dx: 0, opacity: 1 });
    elements.push({ uid: uidSeq++, kind: 'border', id: 'tapa', y: H - SAFE - 4, h: 8, color: 'tapa', opacity: 1 });
    if (get('trio-detailed-brown-v1')) elements.push(fit({ uid: uidSeq++, kind: 'art', id: 'trio-detailed-brown-v1', x: W / 2, y: H / 2 + 3, w: 100, h: 100 / aspectOf(get('trio-detailed-brown-v1')), rot: 0, opacity: 1 }));
    if (get('tiki-sipping-alt-v1')) elements.push(fit({ uid: uidSeq++, kind: 'art', id: 'tiki-sipping-alt-v1', x: 4, y: H / 2, w: 24, h: 24 / aspectOf(get('tiki-sipping-alt-v1')), rot: 0, opacity: 1 }));
    await ensureFont('hanalei', 'Aloha');
    elements.push(fit({ uid: uidSeq++, kind: 'text', text: 'Aloha', font: 'hanalei', size: 14, align: 'center', color: 'lava', x: W * 0.83, y: H / 2, rot: -8, opacity: 1 }));
    commit();
  };
}

// what the cart would have received, with download links (dev only: customers never get files)
let devUrls = [];
function devReport(order, images, set) {
  devUrls.forEach(u => URL.revokeObjectURL(u));
  devUrls = [];
  const kb = n => (n / 1024).toFixed(0) + ' KB';
  const link = (blob, name) => { const u = URL.createObjectURL(blob); devUrls.push(u); return `<a href="${u}" download="${esc(name)}">${esc(name)}</a>`; };
  let html = `<p>dpi ${set.dpi} · ${set.art.Wpx}×${set.art.Hpx}px · request ≈ ${kb(set.total)} of ${kb(BUDGET)} · ${set.ms} ms` +
    (set.omitted.length ? ` · omitted ${set.omitted.join(', ')}` : '') + `</p><p>`;
  for (const [k, v] of Object.entries(images)) html += `${esc(k)} (${kb(v.blob.size)}): ${link(v.blob, v.filename)}<br>`;
  html += `_Design (${order.properties._Design.length} chars): ${link(new Blob([order.properties._Design], { type: 'application/json' }), 'design.json')}<br>`;
  html += `_Design Preview: <a href="${set.preview}" download="design-preview.jpg">design-preview.jpg</a></p>`;
  $('#devOut').innerHTML = html;
  console.log('order', order, images);
}

// =====================================================================================
// boot
// =====================================================================================
(async function boot() {
  renderTabs();
  init3D();
  setGlass(glassKey, false);
  await loadLibrary();
  renderLib();
  const saved = loadSaved();
  if (saved && Array.isArray(saved.elements) && saved.elements.length) {
    const wanted = glassKey;                  // ?glass= wins; the saved design is scaled onto it
    if (GLASSES[saved.glass]) setGlass(saved.glass, false);
    const ok = [];
    for (const el of saved.elements) {
      if (el.kind === 'art' || el.kind === 'pattern') {
        const it = lib.byId.get(el.id);
        if (!it) continue;
        try { it._img = await previewOf(it); } catch (e) { continue; }
      }
      if (el.kind === 'text') await ensureFont(el.font, el.text);
      el.uid = uidSeq++;
      ok.push(fit(el));
    }
    if (!elements.length && ok.length) {      // unless they started adding while we loaded
      elements = ok;
      say('Welcome back. Your design is as you left it.');
    }
    if (params.get('glass') && wanted !== glassKey) setGlass(wanted, true);
  }
  hist.stack = []; hist.i = -1;
  commit();
  changed();
  document.fonts.ready.then(() => { elements.forEach(e => e.kind === 'text' && fit(e)); drawBand(); });
})();
